import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { Subject } from 'rxjs';

const FirebaseOptions = {
	apiKey: environment.FIREBASE_API_KEY,
	authDomain: environment.FIREBASE_AUTH_DOMAIN,
	projectId: environment.FIREBASE_PROJECT_ID,
	storageBucket: environment.FIREBASE_STORAGE_BUCKET,
	messagingSenderId: environment.FIREBASE_MESSAGING_SENDER_ID,
	appId: environment.FIREBASE_APP_ID,
	measurementId: environment.FIREBASE_MEASUREMENT_ID
};

@Injectable({
	providedIn: 'root'
})
export class FireBaseServiceService {

	app = initializeApp(FirebaseOptions);
	messaging = getMessaging(this.app);
	fcmLocalToken: string | null = null;
	/** Emits whenever a push notification arrives while the app is in the foreground */
	newPushNotification$ = new Subject<void>();

	constructor(private router: Router, private ngZone: NgZone) {
		this.fcmLocalToken = localStorage.getItem('fcmToken');
		this.listenToMessages();
	}

	clearFCMToken(): void {
		localStorage.removeItem('fcmToken');
		localStorage.removeItem('fcmTokenTimestamp');
		this.fcmLocalToken = null;
	}

	private isCachedTokenFresh(): boolean {
		const ts = localStorage.getItem('fcmTokenTimestamp');
		if (!ts) return false;
		const ageMs = Date.now() - parseInt(ts, 10);
		const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
		return ageMs < sevenDaysMs;
	}

	async getFCMToken(forceRefresh = false): Promise<string | null> {
		try {
			const cached = localStorage.getItem('fcmToken');
			// Return cached token only if it's fresh and not forced to refresh
			if (cached && !forceRefresh && this.isCachedTokenFresh()) {
				return cached;
			}
			// Clear stale/forced token before fetching fresh one
			this.clearFCMToken();

			let permission = Notification.permission;

			if (permission !== 'granted') {
				permission = await Notification.requestPermission();
			}

			if (permission !== 'granted') {
				return null;
			}
			// Find the specific Firebase SW registration instead of using the generic
			// navigator.serviceWorker.ready (which may resolve to a different SW on staging).
			const swRegistration = await Promise.race([
				(async () => {
					const registrations = await navigator.serviceWorker.getRegistrations();
					const firebaseSW = registrations.find(r =>
						r.active?.scriptURL?.includes('firebase-messaging-sw') ||
						r.installing?.scriptURL?.includes('firebase-messaging-sw') ||
						r.waiting?.scriptURL?.includes('firebase-messaging-sw')
					);
					if (firebaseSW) return firebaseSW;
					// If not yet registered (first page load), fall back to registering now
					return await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
				})(),
				new Promise<null>((_, reject) =>
					setTimeout(() => reject(new Error('SW timeout')), 8000)
				)
			]);

			if (!swRegistration) {
				return null;
			}

			const fcmToken = await Promise.race([
				getToken(this.messaging, {
					vapidKey: environment.FIREBASE_VAPID_KEY,
					serviceWorkerRegistration: swRegistration as ServiceWorkerRegistration
				}),
				new Promise<null>((_, reject) =>
					setTimeout(() => reject(new Error('getToken timeout')), 5000)
				)
			]);

			if (fcmToken) {
				this.fcmLocalToken = fcmToken as string;
				localStorage.setItem('fcmToken', fcmToken as string);
				localStorage.setItem('fcmTokenTimestamp', Date.now().toString());
			}
			return fcmToken as string;

		} catch (error: any) {
			console.error('[FCM] Token error:', error?.message || error);
			return null;
		}
	}

	listenToMessages() {
		onMessage(this.messaging, (payload) => {
			if (Notification.permission === 'granted') {
				const notification = new Notification(payload.notification?.title || 'Notification', {
					body: payload.notification?.body || '',
					icon: '/favicon.ico',
					tag: payload.messageId, // Deduplicate notifications across multiple tabs/listeners
					data: payload.data || {}
				});

				notification.onclick = (event) => {
					event.preventDefault();
					window.focus();
					notification.close();

					this.ngZone.run(() => {
						const data = payload.data || {};
						const contentType = (data['content_type'] || '').toLowerCase();
						const contentId = data['content_id'] && data['content_id'] !== '0' ? data['content_id'] : '';

						switch (contentType) {
							case 'job':
								if (contentId) {
									this.router.navigate(['/student-dashboard/jobs-detail-page', contentId]);
								} else {
									this.router.navigate(['/student-dashboard/jobs']);
								}
								break;
							case 'job_application':
							case 'hiring_complete':
								this.router.navigate(['/student-dashboard/jobs/my-applications']);
								break;
							case 'assessment':
							case 'interview':
								if (contentId) {
									this.router.navigate(['/student-dashboard/jobs/applied', contentId]);
								} else {
									this.router.navigate(['/student-dashboard/jobs/my-applications']);
								}
								break;
							case 'course':
								if (contentId) {
									this.router.navigate(['/student-dashboard/course-details', contentId]);
								} else {
									this.router.navigate(['/student-dashboard']);
								}
								break;
							case 'mentor_session':
							case 'appointment_accepted':
							case 'appointment_rejected':
								this.router.navigate(['/student-dashboard/my-sessions']);
								break;
							case 'subscription_activation':
								this.router.navigate(['/student-dashboard/payment-history']);
								break;
							case 'mock_assessment':
								this.router.navigate(['/student-dashboard/placement-prep']);
								break;
							case 'mock_interview':
								if (contentId && contentId !== '0') {
									if (contentId.includes('/interview.ai/')) {
										const relativePath = contentId.substring(contentId.indexOf('/interview.ai/'));
										window.open(relativePath, '_blank');
									} else {
										window.open(contentId, '_blank');
									}
								} else {
									this.router.navigate(['/student-dashboard/ai-interview-dashboard']);
								}
								break;
							default:
								this.router.navigate(['/student-dashboard']);
								break;
						}
					});
				};
			}
			// Notify subscribers (e.g. navbar) to refresh their notification list
			this.ngZone.run(() => {
				this.newPushNotification$.next();
			});
		});
	}
}